<template>
  <div>
    <v-dialog v-model="dialog" width="1180" persistent scrollable>
      <v-card class="invoice-card">
        <!-- HEADER -->
        <ModalHeader
          title="Add Customer Invoice"
          icon="mdi-receipt-text-plus"
          chip-icon="mdi-account"
          :chip="
            selected_account.customer_name !== 'N/A'
              ? selected_account.customer_name
              : ''
          "
          @close="dialog = false"
        />

        <v-card-text class="pa-4">
          <!-- Access Control Alert -->
          <v-alert v-if="!hasAccess" type="error" text dense class="mb-0">
            <strong>Access Denied.</strong> You don't have permission to create
            invoices. Only Admin, Bookkeeper, and Staff can access this feature.
          </v-alert>

          <div v-if="hasAccess">
            <div class="section-label">
              <v-icon x-small>mdi-account-search</v-icon> Customer
            </div>
            <v-row dense class="mb-2">
              <v-col cols="12" md="8">
                <v-text-field
                  label="Search customer"
                  v-model="customer_search"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  hide-details
                  class="mb-2"
                ></v-text-field>
                <v-data-table
                  :search="customer_search"
                  :items="customers"
                  :headers="customer_headers"
                  :options="{ itemsPerPage: 3 }"
                  dense
                  class="rounded-lg elevation-0 bordered-table"
                >
                  <template v-slot:item.action="{ item }">
                    <v-btn
                      x-small
                      depressed
                      color="primary"
                      @click="select_account(item)"
                      >Select</v-btn
                    >
                  </template>
                </v-data-table>
              </v-col>

              <v-col cols="12" md="4">
                <v-card
                  outlined
                  class="
                    pa-3
                    rounded-lg
                    fill-height
                    d-flex
                    flex-column
                    justify-center
                  "
                >
                  <div class="caption grey--text text-uppercase mb-1">
                    Selected Customer
                  </div>
                  <div
                    class="text-h6 font-weight-medium"
                    :class="
                      selected_account.customer_name === 'N/A'
                        ? 'grey--text'
                        : 'success--text'
                    "
                  >
                    {{ selected_account.customer_name }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-form ref="form">
              <!-- ITEMS TABLE -->
              <div :key="selected_payment_type">
                <div class="section-label mt-2">
                  <v-icon x-small>mdi-file-document-edit</v-icon> Invoice
                  Details
                </div>
                <!-- Purpose & Warranty Row -->
                <v-row dense align="center">
                  <v-col cols="12" sm="5">
                    <v-text-field
                      label="Purpose"
                      prepend-inner-icon="mdi-pen"
                      v-model="purpose"
                      outlined
                      dense
                      hide-details
                      @input="purpose_manual = true"
                    >
                      <template v-slot:append-outer>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              color="deep-orange"
                              v-bind="attrs"
                              v-on="on"
                              @click="
                                purpose =
                                  'Diagnostic Fee of 450. Note: This is creditable but not refundable.';
                                purpose_manual = true;
                              "
                            >
                              <v-icon>mdi-wrench</v-icon>
                            </v-btn>
                          </template>
                          <span>Fill Diagnostic Fee</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-autocomplete
                      v-model="warranty_policy_id"
                      :items="warranty_policies.filter((p) => p.is_active)"
                      item-text="name"
                      item-value="id"
                      label="Warranty Policy *"
                      prepend-inner-icon="mdi-shield-check"
                      :rules="[(v) => !!v || 'Warranty policy is required']"
                      outlined
                      dense
                      hide-details
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle
                            v-if="item.name !== 'Regular Warranty'"
                          >
                            Major: {{ item.major_parts_warranty_value
                            }}{{ item.major_parts_warranty_unit.charAt(0) }} |
                            Minor: {{ item.minor_parts_warranty_value
                            }}{{ item.minor_parts_warranty_unit.charAt(0) }} |
                            Service: {{ item.service_warranty_value
                            }}{{ item.service_warranty_unit.charAt(0) }}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle v-else>
                            Uses each item's own warranty duration
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <!-- Customer Source Row (JMB-125, marketing attribution) -->
                <v-row dense align="center">
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="customer_source_id"
                      :items="customer_sources"
                      item-text="name"
                      item-value="id"
                      label="Customer Source *"
                      prepend-inner-icon="mdi-bullhorn"
                      :rules="[(v) => !!v || 'Customer source is required']"
                      :disabled="is_government_customer"
                      :hint="
                        is_government_customer
                          ? 'Locked: government/institutional customers are always tagged Government / school / office'
                          : ''
                      "
                      :persistent-hint="is_government_customer"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="5" v-if="is_other_source">
                    <v-text-field
                      label="Source note (optional)"
                      prepend-inner-icon="mdi-pen"
                      v-model="source_note"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Customer Aftercare opt-in: tag this sale for follow-up
                     (Installed System / Device Purchase / Repair). The CRM sets
                     the cadence + turn-over/delivery date + mode later. -->
                <v-row dense align="center" class="mt-1">
                  <v-col cols="12">
                    <v-checkbox
                      v-model="register_installed_system"
                      label="Track for Customer Aftercare (maintenance / follow-up)"
                      prepend-icon="mdi-face-agent"
                      hide-details
                      dense
                      class="mt-0"
                      @change="onRegisterToggle"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row dense align="center" v-if="register_installed_system">
                  <v-col cols="12">
                    <v-chip
                      small
                      label
                      :color="is_government_customer ? 'orange' : 'blue'"
                      dark
                    >
                      <v-icon left small>{{
                        is_government_customer ? "mdi-bank" : "mdi-walk"
                      }}</v-icon>
                      {{ is_government_customer ? "Government" : "Walk-in" }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="installed_system_form.category"
                      :items="aftercare_categories"
                      label="Category"
                      prepend-inner-icon="mdi-tag"
                      outlined
                      dense
                      hide-details
                      @change="onAftercareCategoryChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-combobox
                      v-model="installed_system_form.system_type"
                      :items="aftercare_subtypes"
                      label="Type"
                      prepend-inner-icon="mdi-shape"
                      outlined
                      dense
                      hide-details
                    ></v-combobox>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="installed_system_form.site_address"
                      label="Site / Address"
                      hint="Defaults to the customer address if left blank"
                      prepend-inner-icon="mdi-map-marker"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="2">
                    <v-text-field
                      v-model="installed_system_form.site_municipality"
                      label="Municipality"
                      prepend-inner-icon="mdi-city"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" v-if="is_government_customer">
                    <v-text-field
                      v-model="installed_system_form.end_user_contact"
                      label="End User Contact Number"
                      hint="The person on site to call for follow-ups (the account number is the office)"
                      persistent-hint
                      prepend-inner-icon="mdi-phone-account"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <span class="caption grey--text">
                      The CRM will set the cadence, turn-over/delivery date and
                      mode in Customer Aftercare → Pending Setup.
                    </span>
                  </v-col>
                </v-row>

                <!-- Line Items -->
                <div class="section-label mt-3">
                  <v-icon x-small>mdi-cart-plus</v-icon> Add Line Items
                </div>
                <v-row dense align="center">
                  <v-col cols="12" sm="5">
                    <v-text-field
                      label="Search"
                      prepend-inner-icon="mdi-magnify"
                      v-model="item_search"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="payable_source"
                      :items="['Items', 'Services']"
                      label="Source"
                      dense
                      outlined
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      color="success"
                      small
                      depressed
                      :loading="loading"
                      @click="refresh_items"
                    >
                      <v-icon left small>mdi-refresh</v-icon>Refresh
                    </v-btn>
                  </v-col>
                  <!-- SCAN BY SERIAL (JMB-242) — opens a modal to scan serials to Payables -->
                  <v-col cols="auto" v-if="payable_source === 'Items'">
                    <v-btn
                      color="primary"
                      small
                      outlined
                      @click="openSerialSearch"
                    >
                      <v-icon left small>mdi-barcode-scan</v-icon>
                      Scan Serial
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- ITEMS TABLE -->
                <v-data-table
                  v-if="payable_source === 'Items'"
                  v-model="selected_items"
                  checkbox-color="success"
                  show-select
                  :items="items"
                  :headers="item_headers"
                  :search="item_search"
                  :options="table_options"
                  @input="recalculate_total"
                >
                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex">
                      <v-text-field
                        dense
                        label="Quantity"
                        type="number"
                        class="mr-4"
                        ref="quantity"
                        v-model="item.quantity"
                        @input="calc(item)"
                      ></v-text-field>
                    </div>
                  </template>

                  <template v-slot:item.warr="{ item }">
                    <p v-if="item.warranty" class="">
                      {{ item.warranty.warranty_count }}
                      {{ item.warranty.warranty_duration }}
                    </p>
                    <p v-else>No Warranty Issued</p>
                  </template>
                  <template v-slot:item.supplier_warranty="{ item }">
                    <span v-if="item.fifo_lot">
                      <v-chip
                        v-if="item.fifo_lot.warranty_status === 'valid'"
                        color="green"
                        dark
                        x-small
                      >
                        {{
                          formatWarrantyDays(
                            item.fifo_lot.warranty_days_remaining
                          )
                        }}
                      </v-chip>
                      <v-chip
                        v-else-if="
                          item.fifo_lot.warranty_status === 'expiring_soon'
                        "
                        color="amber darken-2"
                        dark
                        x-small
                      >
                        {{ item.fifo_lot.warranty_days_remaining }}d left
                      </v-chip>
                      <v-chip
                        v-else-if="item.fifo_lot.warranty_status === 'expired'"
                        color="red"
                        dark
                        x-small
                      >
                        Expired
                      </v-chip>
                      <v-chip v-else color="grey" x-small>Not set</v-chip>
                    </span>
                    <span v-else class="grey--text caption">-</span>
                  </template>
                </v-data-table>

                <!-- SERVICES TABLE -->
                <v-data-table
                  v-if="payable_source === 'Services'"
                  v-model="selected_services"
                  checkbox-color="success"
                  show-select
                  :items="services"
                  :headers="service_headers"
                  :search="item_search"
                  :options="table_options"
                  @input="recalculate_total"
                >
                  <template v-slot:item.actions="{ item }">
                    <v-text-field
                      dense
                      label="Qty"
                      type="number"
                      v-model="item.quantity"
                      @input="calc_service(item)"
                    ></v-text-field>
                  </template>
                  <template v-slot:item.warr="{ item }">
                    <p v-if="item.warranty">
                      {{ item.warranty.warranty_count }}
                      {{ item.warranty.warranty_duration }}
                    </p>
                    <p v-else>No Warranty</p>
                  </template>
                </v-data-table>
              </div>

              <!-- PAYABLES -->
              <div>
                <div class="section-label mt-3">
                  <v-icon x-small>mdi-format-list-checks</v-icon> Payables
                </div>
                <v-data-table
                  :items="combined_payables"
                  :headers="payable_headers"
                  dense
                  class="bordered-table rounded-lg"
                  show-expand
                  :expanded.sync="expanded_payables"
                  item-key="id"
                >
                  <template v-slot:item.description="{ item }">
                    <span v-if="item.service_name">{{
                      item.service_name
                    }}</span>
                    <span v-else>{{ item.item_name }}</span>
                  </template>
                  <template v-slot:item.amount="{ item }">
                    <v-text-field
                      dense
                      type="number"
                      min="0"
                      hide-details
                      style="max-width: 110px"
                      :value="effectivePrice(item)"
                      @input="setPayableField(item, 'custom_price', $event)"
                    ></v-text-field>
                  </template>
                  <template v-slot:item.quantity="{ item }">
                    <v-text-field
                      dense
                      type="number"
                      min="0"
                      hide-details
                      style="max-width: 80px"
                      :value="payableSource(item).quantity"
                      @input="
                        setPayableField(item, 'quantity', parseInt($event) || 0)
                      "
                    ></v-text-field>
                  </template>
                  <template v-slot:item.serials="{ item }">
                    <template v-if="item.type === 'item' && item.is_serialized">
                      <v-chip
                        small
                        dark
                        :color="
                          serialCount(item) === parseInt(item.quantity)
                            ? 'green'
                            : 'orange'
                        "
                      >
                        {{ serialCount(item) }}/{{ item.quantity }}
                      </v-chip>
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click.stop="openSerialPicker(item)"
                        >Pick</v-btn
                      >
                    </template>
                    <span v-else class="grey--text caption">-</span>
                  </template>
                  <template v-slot:item.lots="{ item }">
                    <template
                      v-if="item.type === 'item' && lot_allocations[item.id]"
                    >
                      <div class="d-flex flex-column">
                        <v-chip
                          v-for="alloc in lot_allocations[item.id]"
                          :key="alloc.lot_id"
                          small
                          color="blue"
                          dark
                          class="mb-1"
                        >
                          Batch {{ alloc.lot_id
                          }}{{
                            alloc.batch_name ? " - " + alloc.batch_name : ""
                          }}
                        </v-chip>
                      </div>
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click.stop="openLotPicker(item)"
                        >Edit</v-btn
                      >
                    </template>
                    <span
                      v-else-if="item.type === 'service'"
                      class="grey--text caption"
                      >-</span
                    >
                    <span v-else class="grey--text caption">No preview</span>
                  </template>
                  <template v-slot:expanded-item="{ headers, item }">
                    <td
                      :colspan="headers.length"
                      class="pa-2"
                      v-if="item.type === 'item' && lot_allocations[item.id]"
                    >
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th>Batch</th>
                            <th>Supplier</th>
                            <th>Purchase Date</th>
                            <th>Unit Cost</th>
                            <th>In Stock</th>
                            <th>Allocated</th>
                            <th>Warranty</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="alloc in lot_allocations[item.id]"
                            :key="alloc.lot_id"
                          >
                            <td>
                              Batch {{ alloc.lot_id
                              }}{{
                                alloc.batch_name ? " - " + alloc.batch_name : ""
                              }}
                            </td>
                            <td>{{ alloc.supplier || "-" }}</td>
                            <td>{{ alloc.purchase_date || "-" }}</td>
                            <td>
                              {{
                                alloc.unit_cost
                                  ? Number(alloc.unit_cost).toLocaleString()
                                  : "-"
                              }}
                            </td>
                            <td>{{ alloc.remaining_quantity }}</td>
                            <td class="font-weight-bold">
                              {{ alloc.allocate_qty }}
                            </td>
                            <td>
                              <v-chip
                                v-if="alloc.warranty_status === 'valid'"
                                color="green"
                                dark
                                x-small
                              >
                                {{
                                  formatWarrantyDays(
                                    alloc.warranty_days_remaining
                                  )
                                }}
                              </v-chip>
                              <v-chip
                                v-else-if="
                                  alloc.warranty_status === 'expiring_soon'
                                "
                                color="amber darken-2"
                                dark
                                x-small
                              >
                                {{ alloc.warranty_days_remaining }}d left
                              </v-chip>
                              <v-chip
                                v-else-if="alloc.warranty_status === 'expired'"
                                color="red"
                                dark
                                x-small
                              >
                                Expired
                              </v-chip>
                              <v-chip v-else color="grey" x-small
                                >Not set</v-chip
                              >
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </td>
                    <td
                      :colspan="headers.length"
                      v-else
                      class="pa-2 grey--text caption"
                    >
                      No batch data available
                    </td>
                  </template>
                </v-data-table>
              </div>

              <!-- LOT PICKER DIALOG -->
              <v-dialog v-model="lot_picker_dialog" max-width="700" persistent>
                <v-card>
                  <v-card-title>
                    Edit Batch Allocation —
                    {{ lot_picker_item ? lot_picker_item.item_name : "" }}
                    <v-spacer></v-spacer>
                    <span class="caption grey--text"
                      >Required: {{ lot_picker_required_qty }}</span
                    >
                  </v-card-title>
                  <v-card-text>
                    <v-alert
                      v-if="lot_picker_error"
                      type="error"
                      dense
                      class="mb-2"
                      >{{ lot_picker_error }}</v-alert
                    >
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Supplier</th>
                          <th>Purchase Date</th>
                          <th>Unit Cost</th>
                          <th>In Stock</th>
                          <th>Warranty</th>
                          <th style="width: 100px">Qty to Allocate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="lot in lot_picker_lots" :key="lot.id">
                          <td>
                            Batch {{ lot.id
                            }}{{
                              lot.import_batch
                                ? " - " + lot.import_batch.batch_description
                                : ""
                            }}
                          </td>
                          <td>{{ lot.supplier || "-" }}</td>
                          <td>
                            {{
                              lot.purchase_date
                                ? lot.purchase_date.substring(0, 10)
                                : "-"
                            }}
                          </td>
                          <td>
                            {{
                              lot.unit_cost
                                ? Number(lot.unit_cost).toLocaleString()
                                : "-"
                            }}
                          </td>
                          <td>{{ lot.remaining_quantity }}</td>
                          <td>
                            <v-chip
                              v-if="lot.warranty_status === 'valid'"
                              color="green"
                              dark
                              x-small
                            >
                              {{
                                formatWarrantyDays(lot.warranty_days_remaining)
                              }}
                            </v-chip>
                            <v-chip
                              v-else-if="
                                lot.warranty_status === 'expiring_soon'
                              "
                              color="amber darken-2"
                              dark
                              x-small
                            >
                              {{ lot.warranty_days_remaining }}d left
                            </v-chip>
                            <v-chip
                              v-else-if="lot.warranty_status === 'expired'"
                              color="red"
                              dark
                              x-small
                            >
                              Expired
                            </v-chip>
                            <v-chip v-else color="grey" x-small>Not set</v-chip>
                          </td>
                          <td>
                            <v-text-field
                              dense
                              type="number"
                              v-model.number="lot._allocate_qty"
                              :max="lot.remaining_quantity"
                              min="0"
                              hide-details
                              style="width: 80px"
                            ></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="6" class="text-right font-weight-bold">
                            Total Allocated:
                          </td>
                          <td
                            :class="{
                              'red--text':
                                lot_picker_total !== lot_picker_required_qty,
                              'green--text':
                                lot_picker_total === lot_picker_required_qty,
                            }"
                            class="font-weight-bold"
                          >
                            {{ lot_picker_total }} /
                            {{ lot_picker_required_qty }}
                          </td>
                        </tr>
                      </tfoot>
                    </v-simple-table>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn text @click="lot_picker_dialog = false"
                      >Cancel</v-btn
                    >
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveLotPicker"
                      >Save Allocation</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- SCAN-BY-SERIAL DIALOG (JMB-242) -->
              <v-dialog
                v-model="serial_search_dialog"
                max-width="520"
                @keydown.esc="serial_search_dialog = false"
              >
                <v-card>
                  <v-card-title>
                    <v-icon left color="primary">mdi-barcode-scan</v-icon>
                    Scan Serial to Add Item
                    <v-spacer></v-spacer>
                    <v-btn icon @click="serial_search_dialog = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="serial_search_input"
                      ref="serialSearchField"
                      label="Scan or type a serial number"
                      placeholder="Scan here, then press Enter"
                      prepend-inner-icon="mdi-barcode-scan"
                      outlined
                      dense
                      clearable
                      autofocus
                      hide-details
                      :loading="serial_search_loading"
                      @keyup.enter="scanSerialToAdd"
                    ></v-text-field>
                    <div
                      v-if="serial_search_feedback"
                      class="caption mt-2"
                      :class="
                        serial_search_feedback_type === 'success'
                          ? 'green--text'
                          : 'red--text'
                      "
                    >
                      <v-icon
                        x-small
                        :color="
                          serial_search_feedback_type === 'success'
                            ? 'green'
                            : 'red'
                        "
                        >{{
                          serial_search_feedback_type === "success"
                            ? "mdi-check-circle"
                            : "mdi-alert-circle"
                        }}</v-icon
                      >
                      {{ serial_search_feedback }}
                    </div>
                    <div v-if="serial_search_log.length" class="mt-3">
                      <div class="caption grey--text mb-1">
                        Scanned this session ({{ serial_search_log.length }})
                      </div>
                      <div class="serial-scan-log">
                        <v-chip
                          v-for="(entry, i) in serial_search_log"
                          :key="i"
                          x-small
                          class="ma-1"
                          color="green lighten-4"
                        >
                          {{ entry }}
                        </v-chip>
                      </div>
                    </div>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="serial_search_dialog = false"
                      >Close</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- SERIAL PICKER DIALOG (JMB-242) -->
              <v-dialog
                v-model="serial_picker_dialog"
                max-width="600"
                persistent
              >
                <v-card>
                  <v-card-title>
                    <v-icon left>mdi-barcode-scan</v-icon>
                    Select Serial Numbers —
                    {{ serial_picker_item ? serial_picker_item.item_name : "" }}
                    <v-spacer></v-spacer>
                    <span class="caption grey--text"
                      >Required: {{ serial_picker_required_qty }}</span
                    >
                  </v-card-title>
                  <v-card-text>
                    <v-alert
                      v-if="serial_picker_error"
                      type="error"
                      dense
                      class="mb-2"
                      >{{ serial_picker_error }}</v-alert
                    >
                    <v-text-field
                      v-model="serial_scan_input"
                      ref="serialScanField"
                      label="Scan serial number, then press Enter"
                      placeholder="Point scanner here and scan"
                      prepend-inner-icon="mdi-barcode-scan"
                      outlined
                      dense
                      autofocus
                      clearable
                      hide-details
                      class="mb-1"
                      @keyup.enter="scanSerial"
                    ></v-text-field>
                    <div
                      v-if="serial_scan_feedback"
                      class="caption mb-2"
                      :class="
                        serial_scan_feedback_type === 'success'
                          ? 'green--text'
                          : 'red--text'
                      "
                    >
                      <v-icon
                        x-small
                        :color="
                          serial_scan_feedback_type === 'success'
                            ? 'green'
                            : 'red'
                        "
                        >{{
                          serial_scan_feedback_type === "success"
                            ? "mdi-check-circle"
                            : "mdi-alert-circle"
                        }}</v-icon
                      >
                      {{ serial_scan_feedback }}
                    </div>
                    <v-autocomplete
                      v-model="serial_picker_selected"
                      :items="serial_picker_options"
                      :loading="serial_picker_loading"
                      label="Scan or search the item's in-stock serials"
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      outlined
                      dense
                    ></v-autocomplete>
                    <div
                      class="caption"
                      :class="
                        serial_picker_selected.length ===
                        serial_picker_required_qty
                          ? 'green--text'
                          : 'orange--text'
                      "
                    >
                      {{ serial_picker_selected.length }} /
                      {{ serial_picker_required_qty }} selected
                    </div>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn text @click="serial_picker_dialog = false"
                      >Cancel</v-btn
                    >
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveSerialPicker"
                      >Save</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- TOTALS & TAX -->
              <div class="section-label mt-3">
                <v-icon x-small>mdi-calculator-variant</v-icon> Totals &amp; Tax
              </div>
              <v-row dense>
                <!-- Left: options -->
                <v-col cols="12" md="7">
                  <div class="caption grey--text mb-1">VAT Treatment</div>
                  <v-radio-group
                    v-model="vat_type"
                    row
                    dense
                    hide-details
                    class="mt-0 mb-3"
                    @change="calculate_vat_based_on_type()"
                  >
                    <v-radio label="VAT Exclusive" value="exclusive"></v-radio>
                    <v-radio
                      label="VAT Inclusive (12%)"
                      value="inclusive"
                    ></v-radio>
                  </v-radio-group>

                  <div class="caption grey--text mb-1">
                    Subject to Other Percentage (SOP)
                  </div>
                  <v-radio-group
                    v-model="has_sop"
                    row
                    dense
                    hide-details
                    class="mt-0"
                    @change="calculate_sop()"
                  >
                    <v-radio label="No SOP" :value="false"></v-radio>
                    <v-radio label="With SOP" :value="true"></v-radio>
                  </v-radio-group>

                  <v-expand-transition>
                    <v-card
                      v-if="has_sop"
                      outlined
                      class="pa-3 mt-2 rounded-lg"
                    >
                      <v-radio-group
                        v-model="sop_type"
                        row
                        dense
                        hide-details
                        class="mt-0 mb-2"
                        @change="calculate_sop()"
                      >
                        <v-radio
                          label="Percentage"
                          value="percentage"
                        ></v-radio>
                        <v-radio label="Fixed Amount" value="amount"></v-radio>
                      </v-radio-group>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            label="SOP Percentage (%)"
                            prepend-inner-icon="mdi-percent"
                            v-model="sop_percentage"
                            @input="calculate_sop()"
                            :readonly="sop_type === 'amount'"
                            :filled="sop_type === 'amount'"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            label="SOP Amount"
                            prepend-inner-icon="mdi-cash"
                            v-model="sop_amount"
                            @input="calculate_sop()"
                            :readonly="sop_type === 'percentage'"
                            :filled="sop_type === 'percentage'"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            label="Cheque Amount"
                            prepend-inner-icon="mdi-checkbook"
                            v-model="cheque_amount"
                            readonly
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            label="VAT Amount (6%)"
                            prepend-inner-icon="mdi-cash-100"
                            v-model="vat"
                            readonly
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-expand-transition>
                </v-col>

                <!-- Right: summary -->
                <v-col cols="12" md="5">
                  <v-card outlined class="pa-3 rounded-lg summary-card">
                    <div class="d-flex align-center mb-2">
                      <span class="body-2 grey--text">Amount</span>
                      <v-spacer></v-spacer>
                      <span class="body-2 font-weight-medium">{{
                        partial_amount
                      }}</span>
                    </div>
                    <v-text-field
                      label="Discount"
                      prepend-inner-icon="mdi-sale"
                      v-model="discount"
                      @input="calculate_discount()"
                      outlined
                      dense
                      hide-details
                      class="mb-2"
                    ></v-text-field>
                    <div class="d-flex align-center mb-2">
                      <span class="body-2 grey--text">Output VAT</span>
                      <v-spacer></v-spacer>
                      <span class="body-2 font-weight-medium">{{ vat }}</span>
                    </div>
                    <v-divider class="mb-2"></v-divider>
                    <div class="d-flex align-center">
                      <span class="subtitle-1 font-weight-bold">Total</span>
                      <v-spacer></v-spacer>
                      <span
                        class="text-h6 font-weight-bold success--text"
                        ref="total_amount"
                        >{{ total_amount }}</span
                      >
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </div>
        </v-card-text>

        <template v-if="hasAccess">
          <v-divider></v-divider>
          <v-card-actions class="px-4 py-3">
            <div class="d-flex align-center">
              <span class="caption grey--text mr-2">Invoice Date</span>
              <input
                type="date"
                v-model="invoice_date"
                class="invoice-date-input"
              />
            </div>
            <span class="caption grey--text ml-3">addInvoiceCard.vue</span>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Close</v-btn>
            <v-btn text @click="reset()">Reset</v-btn>
            <v-btn color="primary" depressed @click="submit" :loading="loading">
              <v-icon left small>mdi-check</v-icon>
              {{ is_recurring ? "Create Recurring Invoice" : "Submit" }}
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// import moment from "moment";

import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import axios from "axios";
export default {
  components: {},
  data() {
    return {
      dialog: false,
      selected_account: { customer_name: "N/A" },
      customer_search: "",
      customer_headers: [
        { text: "Action", value: "action" },
        { text: "Account Name", value: "customer_name" },
        { text: "Date Created", value: "created_at" },
      ],
      // service
      purpose: "Not available",
      // once the user edits Purpose by hand, stop auto-generating it
      purpose_manual: false,
      // Marketing attribution (JMB-125)
      customer_sources: [],
      customer_source_id: null,
      source_note: "",
      table_options: { itemsPerPage: 3 },

      // Customer Aftercare opt-in: tag the sale for follow-up. The CRM sets the
      // cadence + turn-over/delivery date + mode later (Pending Setup).
      register_installed_system: false,
      aftercare_categories: [
        { text: "Installed System", value: "installed_system" },
        { text: "Device Purchase", value: "device_purchase" },
        { text: "Repair", value: "repair" },
      ],
      aftercare_subtypes_by_category: {
        installed_system: ["CCTV", "Starlink", "Solar", "Network", "Server"],
        device_purchase: ["Laptop", "Desktop", "Printer"],
        repair: ["Laptop", "Desktop", "Printer", "CCTV", "Other"],
      },
      installed_system_form: {
        category: "installed_system",
        system_type: "CCTV",
        site_address: "",
        site_municipality: "",
        end_user_contact: "",
      },

      loading: false,
      partial_amount: 0,
      total_amount: 0,

      inputted_quantity: 0,

      // item
      item_payables: [],
      item_search: "",
      item_headers: [
        { text: "", value: "actions", width: 130 },
        { text: "Item Name", value: "item_name" },
        { text: "Item Type", value: "item_type.item_type" },
        { text: "Amount", value: "selling_price" },
        { text: "Total Amount", value: "total_amount" },
        { text: "Stock", value: "quantity" },
        {
          text: "Supplier Warranty",
          value: "supplier_warranty",
          sortable: false,
        },
        { text: "Account Title", value: "chart_of_account.account_title" },
      ],
      vat: 0,
      invoice_date: "",
      discount: 0,
      payable_source: "Items",
      selected_items: [],
      selected_services: [],
      // Set when this card was opened via quotation→invoice conversion;
      // sent as quotation_id on submit so the server links + marks the quote.
      conversion_quote_id: null,
      service_headers: [
        { text: "", value: "actions", width: 130 },
        { text: "Service Name", value: "service_name" },
        { text: "Amount", value: "service_amount" },
        { text: "Total Amount", value: "total_amount" },
        { text: "Account Title", value: "chart_of_account.account_title" },
        { text: "Warranty", value: "warr" },
      ],
      payable_headers: [
        { text: "id", value: "id" },
        { text: "Description", value: "description" },
        { text: "Amount", value: "amount" },
        { text: "Quantity", value: "quantity" },
        { text: "Total", value: "total_amount" },
        { text: "Batches", value: "lots", sortable: false },
        { text: "Serials", value: "serials", sortable: false },
        { text: "", value: "data-table-expand" },
      ],
      payment_type: ["Service", "Item"],
      selected_payment_type: "Items",
      item_count: 3,

      // Recurring invoice data
      is_recurring: false,
      recurring_frequency: "Monthly",
      recurring_interval: 1,
      recurring_start_date: "",
      recurring_end_date: "",
      max_occurrences: null,
      auto_send: false,
      is_active: true,
      recurring_notes: "",
      frequency_options: ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"],

      // SOP data
      has_sop: false,
      sop_type: "percentage", // 'percentage' or 'amount'
      sop_percentage: 15,
      sop_amount: 0,
      cheque_amount: 0,

      // VAT type
      vat_type: "exclusive", // 'exclusive' or 'inclusive'

      // Lot picker
      warranty_policy_id: null,
      lot_allocations: {},
      lot_picker_loading: false,
      lot_preview_timer: null,
      expanded_payables: [],
      lot_picker_dialog: false,
      lot_picker_item: null,
      lot_picker_lots: [],
      lot_picker_required_qty: 0,
      lot_picker_error: "",

      // Serial picker (JMB-242)
      serial_allocations: {},
      serial_picker_dialog: false,
      serial_picker_item: null,
      serial_picker_options: [],
      serial_picker_selected: [],
      serial_picker_required_qty: 0,
      serial_picker_error: "",
      serial_picker_loading: false,
      serial_scan_input: "",
      serial_scan_feedback: "",
      serial_scan_feedback_type: "success",
      serial_search_dialog: false,
      serial_search_input: "",
      serial_search_feedback: "",
      serial_search_feedback_type: "success",
      serial_search_loading: false,
      serial_search_log: [],
    };
  },
  methods: {
    reset() {
      this.clearCustomPrices();
      this.selected_items = [];
      this.selected_services = [];
      this.purpose = "Not available";
      this.purpose_manual = false;
      this.conversion_quote_id = null;
      this.has_sop = false;
      this.sop_type = "percentage";
      this.sop_percentage = 0;
      this.sop_amount = 0;
      this.cheque_amount = 0;
      this.vat_type = "exclusive";
      this.vat = 0;
      this.lot_allocations = {};
      this.warranty_policy_id = null;
      this.expanded_payables = [];
      this.register_installed_system = false;
      this.installed_system_form = {
        category: "installed_system",
        system_type: "CCTV",
        site_address: "",
        site_municipality: "",
        end_user_contact: "",
      };
      this.set_default_warranty_policy();
    },
    // Reset the subtype to the first option whenever the category changes.
    onAftercareCategoryChange(cat) {
      const list = this.aftercare_subtypes_by_category[cat] || [];
      this.installed_system_form.system_type = list[0] || "";
    },
    // Prefill the site from the chosen customer when the opt-in is ticked.
    onRegisterToggle(on) {
      if (!on) return;
      const c = this.selected_account || {};
      if (!this.installed_system_form.site_municipality && c.customer_municipality) {
        this.installed_system_form.site_municipality = c.customer_municipality;
      }
      if (!this.installed_system_form.site_address) {
        this.installed_system_form.site_address = [
          c.customer_purok,
          c.customer_barangay,
          c.customer_municipality,
        ]
          .filter(Boolean)
          .join(", ");
      }
    },
    set_default_warranty_policy() {
      if (this.warranty_policy_id) return;
      const regular = this.warranty_policies.find(
        (p) => p.is_active && p.name === "Regular Warranty"
      );
      if (regular) this.warranty_policy_id = regular.id;
    },
    // custom_price lives on the (store-backed) row objects; scrub it whenever
    // the card is torn down so a one-off price never leaks into the next invoice.
    clearCustomPrices() {
      this.selected_items.concat(this.selected_services).forEach((r) => {
        this.$delete(r, "custom_price");
      });
    },
    // Quotation→invoice conversion entry point (convertToInvoiceLauncher.vue).
    // Rows are full item/service objects (spread copies) with type, quantity
    // and custom_price already resolved from the quote lines.
    openWithQuote({ quotation, customer, rows, vatInclusive }) {
      this.reset();
      if (customer) this.select_account(customer);
      rows.forEach((r) => {
        if (r.type === "service") this.selected_services.push(r);
        else this.selected_items.push(r);
      });
      rows.forEach((r) => {
        if (r.type === "service") this.calc_service(r);
        else this.calc(r);
      });
      const isGov =
        (quotation.quotation_mode || "walk_in") === "government" ||
        !!(customer && customer.is_government);

      // VAT Treatment. Government invoices stay VAT-exclusive and get their VAT
      // through the SOP/cheque block below (its 6% Output VAT) — forcing inclusive
      // double-counts VAT and unbalances the initial journal. Walk-in follows the
      // quote's VAT flag (vatInclusive = walk-in VAT purchase).
      this.vat_type = !isGov && vatInclusive ? "inclusive" : "exclusive";
      this.conversion_quote_id = quotation.id;

      // SOP (which computes the government VAT). Quotation sop_type is
      // 'percentage'|'figure'; the invoice uses 'percentage'|'amount'.
      const quoteSop = parseFloat(quotation.sop_amount) || 0;
      const applyQuoteSop = () => {
        if ((quotation.sop_type || "percentage") === "figure") {
          this.sop_type = "amount";
          this.sop_amount = quoteSop;
        } else {
          this.sop_type = "percentage";
          this.sop_percentage = parseFloat(quotation.sop_value) || 0;
        }
      };
      if (isGov) {
        // Government customers ALWAYS get SOP + VAT (matching a manually created
        // government invoice; select_account() already defaulted 15%). Use the
        // quote's stored SOP settings when it has any, else keep the 15% default.
        this.has_sop = true;
        if (quoteSop > 0) applyQuoteSop();
        else {
          this.sop_type = "percentage";
          if (!this.sop_percentage) this.sop_percentage = 15;
        }
      } else if (quoteSop > 0) {
        // Walk-in quote that carried SOP.
        this.has_sop = true;
        applyQuoteSop();
      } else {
        // Plain walk-in: no SOP.
        this.has_sop = false;
      }

      this.recalculate_total();
      // Now that the total is known, compute the cheque/SOP/VAT amounts so the
      // converted invoice carries them through instead of leaving them at 0.
      if (this.has_sop) this.calculate_sop();
      this.dialog = true;
    },
    calculate_vat_based_on_type() {
      if (this.vat_type === "inclusive") {
        // VAT Inclusive: Extract 12% VAT from the total
        // Formula: VAT = Total / 1.12 * 0.12
        const vatable_amount = this.partial_amount / 1.12;
        // Round to integer to avoid decimal issues in journal entries
        this.vat = Math.round(this.partial_amount - vatable_amount);
      } else {
        // VAT Exclusive: No VAT calculation
        this.vat = 0;
      }
      this.calculate_discount();
    },
    calculate_sop() {
      if (!this.has_sop) {
        this.sop_amount = 0;
        this.sop_percentage = 0;
        this.cheque_amount = 0;
        return;
      }

      // Calculate the cheque amount (vatable amount + 6% VAT)
      // Formula:
      // 1. total_amount / 1.12 = vatable amount
      // 2. total_amount - vatable amount = 12% VAT
      // 3. 12% VAT / 2 = 6% VAT
      // 4. vatable amount + 6% VAT = cheque amount
      // 5. SOP = cheque amount * (sop_percentage / 100)
      const vatable_amount = this.total_amount / 1.12;
      const total_vat = this.total_amount - vatable_amount;
      const six_percent_vat = total_vat / 2;
      const cheque_amount = vatable_amount + six_percent_vat;

      // Round all values to integers to avoid decimal issues in journal entries
      // Store cheque amount for display
      this.cheque_amount = Math.round(cheque_amount);

      // Store the 6% VAT amount
      this.vat = Math.round(six_percent_vat);

      if (this.sop_type === "amount") {
        // Fixed amount mode: user inputs the SOP amount directly.
        // Derive the equivalent percentage so backend recalculation stays
        // consistent (no DB schema change needed).
        this.sop_percentage =
          cheque_amount > 0
            ? Number(((this.sop_amount / cheque_amount) * 100).toFixed(2))
            : 0;
      } else {
        // Percentage mode: compute SOP amount from percentage of cheque amount
        this.sop_amount = Math.round(
          (cheque_amount * this.sop_percentage) / 100
        );
      }
    },
    calculate_cheque_amount() {
      // var vatable = this.total_amount / 1.12;
      // var six_percent = vatable * 0.06;
      // var total_cheque_amount = vatable + six_percent;

      // var vatable = this.total_amount / 1.12;
      var six_percent = this.total_amount * 0.06;
      var total_cheque_amount = this.total_amount + six_percent;

      this.total_amount = total_cheque_amount;
    },
    click(row) {
      console.log(row);
    },
    select_account(item) {
      this.selected_account = item;
      // check if customer.is_government is true
      // if true, set has_sop to true
      if (this.selected_account && this.selected_account.is_government) {
        this.has_sop = true;
        this.sop_percentage = 15; // Default SOP for government
      }
      this.apply_source_default(item);
      return item;
    },
    async get_customer_sources() {
      try {
        let response = await axios.get("/customer_sources");
        this.customer_sources = response.data;
      } catch (e) {
        console.error("Failed to load customer sources", e);
      }
    },
    // Tagging rules (JMB-125): government/institutional buyers are ALWAYS
    // "Government / school / office" (locked — repetition is implied in
    // procurement). Otherwise, a non-government customer with prior invoices
    // pre-selects "Repeat customer" (still editable); new customers get no
    // default.
    async apply_source_default(customer) {
      this.customer_source_id = null;
      this.source_note = "";
      if (!customer || !customer.id) return;
      if (customer.is_government) {
        let gov = this.customer_sources.find(
          (s) => s.name === "Government / school / office"
        );
        if (gov) this.customer_source_id = gov.id;
        return;
      }
      try {
        let response = await axios.get(
          "/customer/" + customer.id + "/has_invoices"
        );
        if (response.data.has_invoices) {
          let repeat = this.customer_sources.find(
            (s) => s.name === "Repeat customer"
          );
          if (repeat && !this.customer_source_id) {
            this.customer_source_id = repeat.id;
          }
        }
      } catch (e) {
        console.error("Failed to check prior invoices", e);
      }
    },

    add_vat() {
      var vat = this.total_amount * 0.12;
      this.total_amount += vat;
      this.vat = vat;
      return;
    },

    non_vat() {
      this.get_item_total();
      this.vat = 0;
      return;
    },
    refresh_items() {
      this.loading = true;
      if (this.payable_source === "Services") {
        this.get_services().then(() => {
          this.loading = false;
        });
      } else {
        this.get_items().then(() => {
          this.loading = false;
        });
      }
    },

    ...mapActions({
      add_invoice: "invoice/add_invoice",
      set_notification: "notification/set_notification",
      get_customers: "customer/get_customers",
      get_items: "item/get_items",
      get_services: "service/get_services",
      get_warranty_policies: "warranty_policy/get_warranty_policies",
    }),
    openSerialSearch() {
      this.serial_search_input = "";
      this.serial_search_feedback = "";
      this.serial_search_log = [];
      this.serial_search_dialog = true;
      this.$nextTick(() => {
        setTimeout(() => {
          const f = this.$refs.serialSearchField;
          if (f && f.focus) f.focus();
        }, 200);
      });
    },
    // Scan a serial number to find its item and add it to Payables (JMB-242).
    // Each scan of a serialized item adds one unit and allocates that exact serial.
    async scanSerialToAdd() {
      const raw = (this.serial_search_input || "").trim();
      if (!raw) return;
      this.serial_search_loading = true;
      this.serial_search_feedback = "";
      try {
        const res = await axios.get("/item_by_serial", {
          params: { serial: raw },
        });
        const serial = res.data.serial_number;
        const foundItem = res.data.item;
        // Prefer the already-loaded item object so the table selection/quantity
        // stay in sync; fall back to the server copy if it isn't loaded.
        let item = this.items.find((i) => i.id === foundItem.id);
        if (!item) {
          item = foundItem;
          this.items.push(item);
        }
        const alloc = this.serial_allocations[item.id] || [];
        if (alloc.includes(serial)) {
          this.serial_search_feedback_type = "error";
          this.serial_search_feedback = `${serial} is already on this invoice (${item.item_name}).`;
        } else {
          const newAlloc = [...alloc, serial];
          this.$set(this.serial_allocations, item.id, newAlloc);
          item.quantity = newAlloc.length;
          if (!this.selected_items.some((i) => i.id === item.id)) {
            this.selected_items.push(item);
          }
          this.calc(item);
          this.serial_search_feedback_type = "success";
          this.serial_search_feedback = `Added ${item.item_name} — S/N ${serial} (qty ${newAlloc.length}).`;
          this.serial_search_log.unshift(`${serial} · ${item.item_name}`);
        }
      } catch (e) {
        this.serial_search_feedback_type = "error";
        if (e.response && e.response.status === 404) {
          this.serial_search_feedback = `No in-stock item found for serial "${raw}".`;
        } else {
          this.serial_search_feedback =
            (e.response && e.response.data && e.response.data.error) ||
            "Serial lookup failed. Please try again.";
        }
      }
      this.serial_search_loading = false;
      this.serial_search_input = "";
      this.$nextTick(() => {
        const f = this.$refs.serialSearchField;
        if (f && f.focus) f.focus();
      });
    },
    calculate_discount() {
      this.total_amount = this.partial_amount - this.discount;
    },
    // combined_payables rows are spread copies; resolve the live source row in
    // selected_items/selected_services so payables-table edits actually persist.
    payableSource(item) {
      const list =
        item.type === "service" ? this.selected_services : this.selected_items;
      return list.find((r) => r.id === item.id) || item;
    },
    basePrice(src, type) {
      return type === "service" ? src.service_amount : src.selling_price;
    },
    effectivePrice(item) {
      const src = this.payableSource(item);
      return src.custom_price !== null &&
        src.custom_price !== undefined &&
        src.custom_price !== ""
        ? parseFloat(src.custom_price)
        : this.basePrice(src, item.type);
    },
    setPayableField(item, field, value) {
      const src = this.payableSource(item);
      this.$set(src, field, value);
      if (item.type === "service") this.calc_service(src);
      else this.calc(src);
    },
    calc(item) {
      const price =
        item.custom_price !== null &&
        item.custom_price !== undefined &&
        item.custom_price !== ""
          ? parseFloat(item.custom_price)
          : item.selling_price;
      item.total_amount = price * item.quantity;
      var index2 = this.items.findIndex((a) => a.id == item.id);
      if (index2 >= 0) this.items.splice(index2, 1, item);
      this.recalculate_total();
    },

    calc_service(service) {
      const price =
        service.custom_price !== null &&
        service.custom_price !== undefined &&
        service.custom_price !== ""
          ? parseFloat(service.custom_price)
          : service.service_amount;
      service.total_amount = price * (service.quantity || 1);
      var index = this.services.findIndex((a) => a.id == service.id);
      if (index >= 0) this.services.splice(index, 1, service);
      this.recalculate_total();
    },

    recalculate_total() {
      let total = 0;
      this.selected_items.forEach((i) => {
        // Fall back to (custom) price * quantity when the quantity field was
        // never edited (calc() hasn't run yet, so total_amount is undefined).
        const price =
          i.custom_price !== null &&
          i.custom_price !== undefined &&
          i.custom_price !== ""
            ? parseFloat(i.custom_price)
            : i.selling_price;
        total += i.total_amount ?? price * (i.quantity || 1);
      });
      this.selected_services.forEach((s) => {
        const price =
          s.custom_price !== null &&
          s.custom_price !== undefined &&
          s.custom_price !== ""
            ? parseFloat(s.custom_price)
            : s.service_amount;
        total += s.total_amount ?? price * (s.quantity || 1);
      });
      this.partial_amount = total;
      this.calculate_vat_based_on_type();
      // Keep the Purpose in sync with the current line-up (unless typed by hand).
      this.autofillPurpose();
      // Auto-fetch FIFO preview (debounced to avoid spamming on rapid changes)
      this.debouncedFetchFIFO();
    },
    // Auto-generate the invoice Purpose from its items/services. Runs on every
    // line change (add/remove/quotation conversion) but never clobbers a Purpose
    // the user typed themselves (purpose_manual flag).
    autofillPurpose() {
      if (this.purpose_manual) return;
      this.purpose = this.generatePurpose();
    },
    generatePurpose() {
      // 650 is the walk-in tax-fee line item — never part of the description.
      const TAX_FEE_ID = 650;
      const itemNames = this.selected_items
        .filter((i) => i.id !== TAX_FEE_ID)
        .map((i) => (i.item_name || "").trim())
        .filter(Boolean);
      const serviceNames = this.selected_services
        .map((s) => (s.service_name || "").trim())
        .filter(Boolean);

      if (!itemNames.length && !serviceNames.length) return "Not available";

      // Services only → Payment for "Name" (comma-joined when several).
      if (!itemNames.length) {
        return "Payment for " + serviceNames.map((n) => `"${n}"`).join(", ");
      }

      // Single item, no services → Purchase of <item>.
      if (itemNames.length === 1 && !serviceNames.length) {
        return `Purchase of ${itemNames[0]}`;
      }

      // Group / mixed → Purchase of A, B, C[, Services][, and other Items...].
      const MAX_NAMES = 3;
      const shown = itemNames.slice(0, MAX_NAMES);
      let parts = shown.join(", ");
      if (serviceNames.length) parts += ", Services";
      if (itemNames.length > shown.length) parts += ", and other Items...";
      return `Purchase of ${parts}`;
    },
    debouncedFetchFIFO() {
      clearTimeout(this.lot_preview_timer);
      this.lot_preview_timer = setTimeout(() => {
        this.fetchFIFOPreview();
      }, 500);
    },

    get_item_total() {
      this.recalculate_total();
    },

    async submit() {
      // Check access before submitting
      if (!this.hasAccess) {
        this.$alert({
          title: "Error",
          message:
            "Access Denied! You don't have permission to create invoices.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }

      // check if customer is selected
      if (this.selected_account.id == null) {
        this.$alert({
          title: "Validation",
          message: "No customer selected",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Check if warranty policy is selected
      if (!this.warranty_policy_id) {
        this.$alert({
          title: "Validation",
          message: "Please select a warranty policy",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Customer source is required on every new invoice (JMB-125)
      if (!this.customer_source_id) {
        this.$alert({
          title: "Validation",
          message:
            "Please select where the customer came from (Customer Source)",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Guard (JMB-153): an invoice must have at least one item or service.
      // A header-only invoice (amount but no line items) has no valid
      // double-entry journal and would post a lone Accounts Receivable debit,
      // silently unbalancing the daily journal.
      if (!this.combined_payables || this.combined_payables.length === 0) {
        this.$alert({
          title: "Validation",
          message:
            "Add at least one item or service before saving the invoice.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Add recurring invoice data if enabled
      if (this.is_recurring) {
        this.$alert({
          title: "Validation",
          message: "Please check all fields",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Serialized items must have exactly qty serials picked (JMB-242)
      for (const p of this.combined_payables) {
        if (p.type !== "item" || !p.is_serialized) continue;
        const picked = this.serial_allocations[p.id] || [];
        if (picked.length !== parseInt(p.quantity)) {
          this.$alert({
            title: "Serial Numbers",
            message: `${p.item_name} is serialized: pick ${p.quantity} serial number(s) in the Serials column (${picked.length} selected).`,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }

      // Fetch FIFO preview before confirming (if not already loaded)
      const hasItemPayables = this.combined_payables.some(
        (p) => p.type === "item" && p.quantity > 0
      );
      if (hasItemPayables && Object.keys(this.lot_allocations).length === 0) {
        await this.fetchFIFOPreview();
      }

      let x = await this.$confirm({
        title: "Submit Invoice",
        message: "Are you sure you want to proceed?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (!x) return;

      // Build lot_allocations payload: { item_id: [{lot_id, quantity}] }
      const lotAllocPayload = {};
      for (const [itemId, allocs] of Object.entries(this.lot_allocations)) {
        lotAllocPayload[itemId] = allocs.map((a) => ({
          lot_id: a.lot_id,
          quantity: a.allocate_qty,
        }));
      }

      let form = {
        customer_id: this.selected_account.id,
        discount: this.discount,
        amount: this.partial_amount,
        total_amount: this.total_amount,
        invoice_date: this.invoice_date,
        is_invoice: 1,
        payables: this.combined_payables,
        purpose: this.purpose,
        inserted_by: this.user.full_name,
        vat: this.vat,
        vat_type: this.vat_type,
        with_sop: this.has_sop,
        sop_percentage: this.has_sop ? this.sop_percentage : 0,
        sop_amount: this.has_sop ? this.sop_amount : 0,
        cheque_amount: this.has_sop ? this.cheque_amount : 0,
        lot_allocations: lotAllocPayload,
        serial_allocations: this.serial_allocations,
        warranty_policy_id: this.warranty_policy_id,
        customer_source_id: this.customer_source_id,
        source_note: this.is_other_source ? this.source_note : null,
        quotation_id: this.conversion_quote_id,
        register_installed_system: this.register_installed_system,
        installed_system: this.register_installed_system
          ? {
              category: this.installed_system_form.category,
              system_type: this.installed_system_form.system_type,
              site_address: this.installed_system_form.site_address,
              site_municipality: this.installed_system_form.site_municipality,
              end_user_contact: this.is_government_customer
                ? this.installed_system_form.end_user_contact
                : null,
            }
          : null,
      };

      this.loading = true;
      try {
        const convertingQuoteId = this.conversion_quote_id;
        await this.add_invoice(form);
        this.loading = false;
        this.clearCustomPrices();
        this.lot_allocations = {};
        this.serial_allocations = {};
        this.customer_source_id = null;
        this.source_note = "";
        this.conversion_quote_id = null;
        if (convertingQuoteId) {
          // refresh so the quotations table shows the new "Invoiced" state
          this.$store.dispatch("quotation/get_quotations");
        }
        return this.set_notification({
          message: "You have successfully inserted a new data",
          type: "success",
        });
      } catch (e) {
        this.loading = false;
        // Handle 422 stock validation errors from server
        if (e.response && e.response.status === 422 && e.response.data.items) {
          let msgs = e.response.data.items.map(
            (i) =>
              `${i.item_name}: Available ${i.available}, Requested ${i.requested}`
          );
          this.$alert({
            title: "Error",
            message:
              "Insufficient stock for one or more items:\n" + msgs.join("\n"),
            color: "error",
            icon: "mdi-alert-circle",
          });
          return;
        }
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && e.response.data.message) ||
            String(e),
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
    },
    async fetchFIFOPreview() {
      const itemPayables = this.combined_payables.filter(
        (p) => p.type === "item" && p.quantity > 0
      );
      if (itemPayables.length === 0) {
        this.lot_allocations = {};
        return;
      }

      this.lot_picker_loading = true;
      try {
        const items = itemPayables.map((p) => ({
          item_id: p.id,
          quantity: parseInt(p.quantity),
        }));
        const response = await axios.post("/invoice/fifo_preview", { items });
        const previews = response.data;

        const newAllocations = {};
        for (const [itemId, preview] of Object.entries(previews)) {
          if (preview.allocations && preview.allocations.length > 0) {
            newAllocations[itemId] = preview.allocations;
          }
        }
        this.lot_allocations = newAllocations;
      } catch (e) {
        console.error("FIFO preview failed:", e);
      }
      this.lot_picker_loading = false;
    },
    // Serial picker (JMB-242)
    serialCount(item) {
      return (this.serial_allocations[item.id] || []).length;
    },
    async openSerialPicker(item) {
      this.serial_picker_item = item;
      this.serial_picker_required_qty = parseInt(item.quantity) || 0;
      this.serial_picker_error = "";
      this.serial_scan_input = "";
      this.serial_scan_feedback = "";
      this.serial_picker_selected = [
        ...(this.serial_allocations[item.id] || []),
      ];
      this.serial_picker_dialog = true;
      this.serial_picker_loading = true;
      try {
        const response = await axios.get(`/item/${item.id}/serials`);
        this.serial_picker_options = response.data
          .filter((s) => s.status === "in_stock")
          .map((s) => s.serial_number);
      } catch (e) {
        console.error("Failed to load serials", e);
        this.serial_picker_options = [];
      }
      this.serial_picker_loading = false;
    },
    scanSerial() {
      const raw = (this.serial_scan_input || "").trim();
      if (!raw) return;
      // Match the scanned code against the item's in-stock serials (case-insensitive)
      const match = this.serial_picker_options.find(
        (s) => s.toLowerCase() === raw.toLowerCase()
      );
      if (!match) {
        this.serial_scan_feedback_type = "error";
        this.serial_scan_feedback = `"${raw}" is not an in-stock serial for this item.`;
      } else if (this.serial_picker_selected.includes(match)) {
        this.serial_scan_feedback_type = "error";
        this.serial_scan_feedback = `${match} is already selected.`;
      } else if (
        this.serial_picker_selected.length >= this.serial_picker_required_qty
      ) {
        this.serial_scan_feedback_type = "error";
        this.serial_scan_feedback = `Already have ${this.serial_picker_required_qty} serial(s) — remove one before scanning more.`;
      } else {
        this.serial_picker_selected.push(match);
        this.serial_scan_feedback_type = "success";
        this.serial_scan_feedback = `Added ${match} (${this.serial_picker_selected.length}/${this.serial_picker_required_qty}).`;
      }
      this.serial_scan_input = "";
      // Keep focus on the field for the next scan
      this.$nextTick(() => {
        const f = this.$refs.serialScanField;
        if (f && f.focus) f.focus();
      });
    },
    saveSerialPicker() {
      if (
        this.serial_picker_selected.length !== this.serial_picker_required_qty
      ) {
        this.serial_picker_error = `Select exactly ${this.serial_picker_required_qty} serial number(s) — ${this.serial_picker_selected.length} selected`;
        return;
      }
      this.$set(this.serial_allocations, this.serial_picker_item.id, [
        ...this.serial_picker_selected,
      ]);
      this.serial_picker_dialog = false;
    },
    openLotPicker(item) {
      this.lot_picker_item = item;
      this.lot_picker_required_qty = parseInt(item.quantity) || 0;
      this.lot_picker_error = "";

      // Build lots list from the item's active_stock_lots (eager-loaded)
      const activeLots = item.active_stock_lots || [];
      const currentAllocs = this.lot_allocations[item.id] || [];

      this.lot_picker_lots = activeLots.map((lot) => {
        const existing = currentAllocs.find((a) => a.lot_id === lot.id);
        return {
          ...lot,
          purchase_date: lot.purchase_date
            ? lot.purchase_date.substring(0, 10)
            : null,
          _allocate_qty: existing ? existing.allocate_qty : 0,
        };
      });

      this.lot_picker_dialog = true;
    },
    saveLotPicker() {
      const total = this.lot_picker_lots.reduce(
        (sum, lot) => sum + (parseInt(lot._allocate_qty) || 0),
        0
      );
      if (total !== this.lot_picker_required_qty) {
        this.lot_picker_error = `Total allocated (${total}) must equal required quantity (${this.lot_picker_required_qty})`;
        return;
      }

      // Validate no lot exceeds remaining
      for (const lot of this.lot_picker_lots) {
        const qty = parseInt(lot._allocate_qty) || 0;
        if (qty > lot.remaining_quantity) {
          this.lot_picker_error = `Batch #${lot.id} only has ${lot.remaining_quantity} in stock`;
          return;
        }
      }

      const itemId = this.lot_picker_item.id;
      const allocations = this.lot_picker_lots
        .filter((lot) => (parseInt(lot._allocate_qty) || 0) > 0)
        .map((lot) => ({
          lot_id: lot.id,
          batch_name: lot.import_batch
            ? lot.import_batch.batch_description
            : null,
          supplier: lot.supplier,
          purchase_date: lot.purchase_date,
          unit_cost: lot.unit_cost,
          remaining_quantity: lot.remaining_quantity,
          allocate_qty: parseInt(lot._allocate_qty),
          warranty_status: lot.warranty_status,
          warranty_days_remaining: lot.warranty_days_remaining,
        }));

      this.$set(this.lot_allocations, itemId, allocations);
      this.lot_picker_dialog = false;
    },
    formatWarrantyDays(days) {
      if (days == null) return "";
      if (days >= 30) return Math.floor(days / 30) + "mo left";
      return days + "d left";
    },
    formatted_warranty(service) {
      let duration = service.warranty.warranty_duration;
      let count = service.warranty.warranty_count;
      return count + " " + duration;
    },

    // Recurring invoice methods
    toggleRecurring() {
      if (this.is_recurring && !this.recurring_start_date) {
        this.recurring_start_date = moment().format("YYYY-MM-DD");
      }
    },

    formatDate(date) {
      return moment(date).format("MMM DD, YYYY");
    },

    calculateNextDates() {
      if (!this.recurring_start_date || !this.recurring_frequency) {
        return [];
      }

      const dates = [];
      let currentDate = moment(this.recurring_start_date);
      const endDate = this.recurring_end_date
        ? moment(this.recurring_end_date)
        : null;
      const maxOccurrences = this.max_occurrences || 5; // Show max 5 preview dates

      for (let i = 0; i < maxOccurrences; i++) {
        if (endDate && currentDate.isAfter(endDate)) {
          break;
        }

        dates.push(currentDate.format("YYYY-MM-DD"));

        // Calculate next date based on frequency
        switch (this.recurring_frequency.toLowerCase()) {
          case "daily":
            currentDate.add(this.recurring_interval, "days");
            break;
          case "weekly":
            currentDate.add(this.recurring_interval, "weeks");
            break;
          case "monthly":
            currentDate.add(this.recurring_interval, "months");
            break;
          case "quarterly":
            currentDate.add(this.recurring_interval * 3, "months");
            break;
          case "yearly":
            currentDate.add(this.recurring_interval, "years");
            break;
        }
      }

      return dates;
    },

    resetRecurringFields() {
      this.is_recurring = false;
      this.recurring_frequency = "Monthly";
      this.recurring_interval = 1;
      this.recurring_start_date = "";
      this.recurring_end_date = "";
      this.max_occurrences = null;
      this.auto_send = false;
      this.is_active = true;
      this.recurring_notes = "";
    },
  },
  created() {
    this.invoice_date = moment().format("Y-MM-DD");
    this.get_customer_sources();
    if (this.customers.length <= 0) this.get_customers();
    if (this.services.length <= 0) this.get_services();
    if (this.warranty_policies.length <= 0) {
      this.get_warranty_policies().then(() =>
        this.set_default_warranty_policy()
      );
    } else {
      this.set_default_warranty_policy();
    }
  },
  computed: {
    aftercare_subtypes() {
      return (
        this.aftercare_subtypes_by_category[
          this.installed_system_form.category
        ] || []
      );
    },
    latest_invoice() {
      return this.invoices[0];
    },
    is_other_source() {
      let selected = this.customer_sources.find(
        (s) => s.id === this.customer_source_id
      );
      return !!selected && selected.name === "Other";
    },
    is_government_customer() {
      return !!(this.selected_account && this.selected_account.is_government);
    },
    preview_dates() {
      return this.calculateNextDates();
    },
    combined_payables() {
      const items = this.selected_items.map((i) => ({
        ...i,
        type: "item",
        total_amount:
          i.total_amount ??
          (i.custom_price !== null &&
          i.custom_price !== undefined &&
          i.custom_price !== ""
            ? parseFloat(i.custom_price)
            : i.selling_price) * (i.quantity || 1),
      }));
      const services = this.selected_services.map((s) => ({
        ...s,
        type: "service",
        total_amount:
          s.total_amount ??
          (s.custom_price !== null &&
          s.custom_price !== undefined &&
          s.custom_price !== ""
            ? parseFloat(s.custom_price)
            : s.service_amount) * (s.quantity || 1),
      }));
      return [...items, ...services];
    },
    lot_picker_total() {
      return this.lot_picker_lots.reduce(
        (sum, lot) => sum + (parseInt(lot._allocate_qty) || 0),
        0
      );
    },
    hasAccess() {
      // Gated purely by the add_invoice.access permission key (hat bundle /
      // Role Restrictions); accounts without hats get no access.
      return this.hasPermission("add_invoice.access");
    },
    ...mapGetters({
      customers: "customer/customers",
      items: "item/items",
      services: "service/services",
      invoices: "invoice/invoices",
      user: "auth/user",
      user_permissions: "permission/user_permissions",
      hasPermission: "permission/hasPermission",
      warranty_policies: "warranty_policy/warranty_policies",
    }),
  },
};
</script>

<style lang="scss" scoped>
// Section labels, bordered tables, summary cards & date-input styling are
// provided globally by src/styles/design-system.scss (see DESIGN_GUIDELINES.md).
.serial-scan-log {
  max-height: 140px;
  overflow-y: auto;
}
</style>
